import { fmt } from "./formatting";
import {
  convertMoney,
  getMoneyContext,
  getPlatformCurrency,
  normalizeCurrency,
} from "./currency";

export const parseMoney = (value) => {
  const n = Number(String(value || "").replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const localDateKey = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const platformTotal = (platforms = {}, options = {}) => {
  const moneyContext = getMoneyContext(options);
  return Object.entries(platforms).reduce(
    (sum, [platform, value]) =>
      sum +
      convertMoney(
        parseMoney(value),
        getPlatformCurrency(platform),
        moneyContext.displayCurrency,
        moneyContext.usdBrlRate
      ),
    0
  );
};

export const withdrawalsTotal = (withdrawals = {}, options = {}) => {
  const moneyContext = getMoneyContext(options);
  return Object.entries(withdrawals).reduce(
    (sum, [platform, value]) =>
      sum +
      convertMoney(
        parseMoney(value),
        getPlatformCurrency(platform),
        moneyContext.displayCurrency,
        moneyContext.usdBrlRate
      ),
    0
  );
};

export const depositsTotal = (deposits = {}, options = {}) => {
  const moneyContext = getMoneyContext(options);
  return Object.entries(deposits).reduce(
    (sum, [platform, value]) =>
      sum +
      convertMoney(
        parseMoney(value),
        getPlatformCurrency(platform),
        moneyContext.displayCurrency,
        moneyContext.usdBrlRate
      ),
    0
  );
};

const getAccountCurrency = (account, field) =>
  normalizeCurrency(
    account?.[`${field}Currency`] || account?.currency,
    getPlatformCurrency(account?.name)
  );

const convertAccountValue = (account, field, options = {}) => {
  const moneyContext = getMoneyContext(options);
  return convertMoney(
    parseMoney(account?.[field]),
    getAccountCurrency(account, field),
    moneyContext.displayCurrency,
    moneyContext.usdBrlRate
  );
};

export const accountBalanceTotal = (accounts = [], options = {}) =>
  accounts.reduce(
    (sum, account) => sum + convertAccountValue(account, "balance", options),
    0
  );

export const accountAdjustedTotal = (accounts = [], options = {}) =>
  accounts.reduce(
    (sum, account) =>
      sum +
      convertAccountValue(account, "balance", options) +
      convertAccountValue(account, "withdrawn", options) -
      convertAccountValue(account, "deposited", options),
    0
  );

export const adjustedPlatformTotal = (bankroll = {}, options = {}) =>
  Array.isArray(bankroll.accounts)
    ? accountAdjustedTotal(bankroll.accounts, options)
    : platformTotal(bankroll.platforms, options) +
      withdrawalsTotal(bankroll.withdrawals, options) -
      depositsTotal(bankroll.deposits, options);

export const getHistoryAdjustedTotal = (entry = {}, options = {}) => {
  if (entry.adjustedTotal !== undefined && entry.adjustedTotal !== null) {
    const moneyContext = getMoneyContext(options);

    return convertMoney(
      parseMoney(entry.adjustedTotal),
      normalizeCurrency(
        entry.adjustedTotalCurrency || entry.adjusted_total_currency,
        moneyContext.displayCurrency
      ),
      moneyContext.displayCurrency,
      moneyContext.usdBrlRate
    );
  }

  return adjustedPlatformTotal(entry, options);
};

export const getBankrollBaseValue = (bankroll = {}, options = {}) => {
  const moneyContext = getMoneyContext(options);
  return convertMoney(
    parseMoney(bankroll.base),
    normalizeCurrency(bankroll.baseCurrency, moneyContext.displayCurrency),
    moneyContext.displayCurrency,
    moneyContext.usdBrlRate
  );
};

export const formatDelta = (value, currency) => {
  const formatted = fmt(Math.abs(value), currency);
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return formatted;
};

const formatMoneyInputValue = (value) => {
  const normalized = Number(value.toFixed(2));
  return Number.isInteger(normalized) ? String(normalized) : String(normalized);
};

export const applyPlatformWithdrawal = (bankroll, platform, value) => {
  if (Array.isArray(bankroll?.accounts)) {
    return applyAccountWithdrawal(bankroll, platform, value);
  }

  const amount = parseMoney(value);
  const currentBalance = parseMoney(bankroll?.platforms?.[platform]);
  const withdrawal = Math.min(amount, currentBalance);

  if (!platform || withdrawal <= 0) return bankroll;

  const nextBalance = currentBalance - withdrawal;
  const currentWithdrawn = parseMoney(bankroll?.withdrawals?.[platform]);

  return withTodaySnapshot({
    ...bankroll,
    platforms: {
      ...(bankroll.platforms || {}),
      [platform]: formatMoneyInputValue(nextBalance),
    },
    withdrawals: {
      ...(bankroll.withdrawals || {}),
      [platform]: formatMoneyInputValue(currentWithdrawn + withdrawal),
    },
  });
};

export const applyPlatformDeposit = (bankroll, platform, value) => {
  if (Array.isArray(bankroll?.accounts)) {
    return applyAccountDeposit(bankroll, platform, value);
  }

  const deposit = parseMoney(value);
  const currentBalance = parseMoney(bankroll?.platforms?.[platform]);

  if (!platform || deposit <= 0) return bankroll;

  const currentDeposited = parseMoney(bankroll?.deposits?.[platform]);

  return withTodaySnapshot({
    ...bankroll,
    platforms: {
      ...(bankroll.platforms || {}),
      [platform]: formatMoneyInputValue(currentBalance + deposit),
    },
    deposits: {
      ...(bankroll.deposits || {}),
      [platform]: formatMoneyInputValue(currentDeposited + deposit),
    },
  });
};

export const applyAccountBalance = (bankroll, accountId, value) =>
  withTodaySnapshot({
    ...bankroll,
    accounts: (bankroll.accounts || []).map((account) =>
      account.id === accountId
        ? { ...account, balance: sanitizeMoneyText(value) }
        : account
    ),
  });

export const applyAccountWithdrawal = (bankroll, accountId, value) => {
  const amount = parseMoney(value);
  if (!accountId || amount <= 0) return bankroll;

  return withTodaySnapshot({
    ...bankroll,
    accounts: (bankroll.accounts || []).map((account) => {
      if (account.id !== accountId) return account;

      const currentBalance = parseMoney(account.balance);
      const withdrawal = Math.min(amount, currentBalance);
      if (withdrawal <= 0) return account;

      return {
        ...account,
        balance: formatMoneyInputValue(currentBalance - withdrawal),
        withdrawn: formatMoneyInputValue(parseMoney(account.withdrawn) + withdrawal),
      };
    }),
  });
};

export const applyAccountDeposit = (bankroll, accountId, value) => {
  const deposit = parseMoney(value);
  if (!accountId || deposit <= 0) return bankroll;

  return withTodaySnapshot({
    ...bankroll,
    accounts: (bankroll.accounts || []).map((account) =>
      account.id === accountId
        ? {
            ...account,
            balance: formatMoneyInputValue(parseMoney(account.balance) + deposit),
            deposited: formatMoneyInputValue(parseMoney(account.deposited) + deposit),
          }
        : account
    ),
  });
};

const sanitizeMoneyText = (value) => {
  const text = String(value ?? "");
  if (text.includes("-")) return "";
  return text.slice(0, 12);
};

export const withTodaySnapshot = (bankroll) => {
  const today = localDateKey();
  const snapshot = {
    date: today,
    base: bankroll.base,
    baseCurrency: bankroll.baseCurrency,
    accounts: Array.isArray(bankroll.accounts)
      ? bankroll.accounts.map((account) => ({ ...account }))
      : undefined,
    platforms: { ...(bankroll.platforms || {}) },
    withdrawals: { ...(bankroll.withdrawals || {}) },
    deposits: { ...(bankroll.deposits || {}) },
  };
  const history = Array.isArray(bankroll.history) ? bankroll.history : [];
  const hasPlatformSnapshot = Array.isArray(snapshot.accounts)
    ? snapshot.accounts.some((account) => String(account.balance || "").trim() !== "")
    : Object.values(snapshot.platforms).some(
        (value) => String(value || "").trim() !== ""
      );

  if (!hasPlatformSnapshot) {
    return {
      ...bankroll,
      history: history.filter((entry) => entry.date !== today),
    };
  }

  return {
    ...bankroll,
    history: [
      ...history.filter((entry) => entry.date !== today),
      snapshot,
    ].sort((a, b) => a.date.localeCompare(b.date)),
  };
};

export const simpleChartThemes = {
  dark: {
    text: "rgba(232,220,200,0.68)",
    soft: "rgba(232,220,200,0.48)",
    grid: "rgba(232,220,200,0.14)",
    accent: "#ce9210",
    panel: "#000000fa",
    border: "#da860715",
    positive: "#5de0a3",
    danger: "#ff6b5f",
    positiveFill: "rgba(93,224,163,0.12)",
    dangerFill: "rgba(255,107,95,0.12)",
  },
  light: {
    text: "rgba(28,24,18,0.82)",
    soft: "rgba(28,24,18,0.68)",
    grid: "rgba(28,24,18,0.22)",
    accent: "#6f470c",
    panel: "#f8faf9",
    border: "rgba(147,105,28,0.2)",
    positive: "#155f3c",
    danger: "#8f221b",
    positiveFill: "rgba(21,95,60,0.14)",
    dangerFill: "rgba(143,34,27,0.14)",
  },
};
